import { useAppDispatch } from '_store';
import auditReducer from '_store/audit/auditReducers';
import {
  auditCurrentPageSelector,
  auditFiltersSelector,
  auditItemsPerPageSelector,
  auditListSelector,
} from '_store/audit/selectors';
import { useAuditLogsRequest } from '_store/audit/utils';
import { PaginationComponent } from '_store/components/PaginationComponent';
import usePagination from '_store/components/usePagination';
import { useLocalization } from '@fluent/react';
import {
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  actionColor,
  actionLabelKey,
  auditActor,
  metadataRaw,
  metadataSummary,
} from '../../../items/AuditHistory/utils';
import AuditListFilters from './Filters';
import styles from './styles';

// The text filters dispatch on every keystroke; without this the list would refetch per
// typed character.
const SEARCH_DEBOUNCE_MS = 400;

const COLUMNS = [
  'audit-column-date',
  'audit-column-user',
  'audit-column-action',
  'audit-column-resource-type',
  'audit-column-resource-id',
  'audit-column-details',
];

const AuditListLayout: FC = () => {
  const classes = styles();
  const theme = useTheme();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();

  const list = useSelector(auditListSelector);
  const filters = useSelector(auditFiltersSelector);
  const storedCurrentPage = useSelector(auditCurrentPageSelector);
  const storedItemsPerPage = useSelector(auditItemsPerPageSelector);

  const [itemsPerPage, setItemsPerPage] = useState(storedItemsPerPage);

  const { currentPage, totalPages, goToPage } = usePagination(
    list.total,
    itemsPerPage,
    storedCurrentPage,
  );
  const dispatchAuditLogsRequest = useAuditLogsRequest({ currentPage, itemsPerPage });

  useEffect(() => {
    const handle = setTimeout(dispatchAuditLogsRequest, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [filters, dispatchAuditLogsRequest]);

  // A filter change resets the stored page; mirror it into the local pagination hook
  useEffect(() => {
    if (storedCurrentPage !== currentPage) {
      goToPage(storedCurrentPage);
    }
  }, [storedCurrentPage]);

  useEffect(() => {
    dispatch(auditReducer.actions.setPaginationItemsPerPage(itemsPerPage));
  }, [itemsPerPage]);

  // fetchedAt 0 covers the debounce window before the very first request goes out
  const loading = list.loading || list.fetchedAt === 0;

  const body = () => {
    if (loading && list.results.length === 0) {
      return (
        <div className={classes.spinner}>
          <CircularProgress />
        </div>
      );
    }

    if (list.error && list.results.length === 0) {
      return (
        <Typography variant="body2" className={classes.statusError}>
          {l10n.getString('audit-error')}
        </Typography>
      );
    }

    if (list.results.length === 0) {
      return (
        <Typography variant="body2" className={classes.status}>
          {l10n.getString('audit-no-results')}
        </Typography>
      );
    }

    return (
      // Plain TableCell headers, not TableSortLabel: the server sorts createdAt DESC and
      // exposes no sort parameter, so nothing here must look clickable.
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column} className={classes.headCell}>
                {l10n.getString(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {list.results.map((entry) => {
            const meta = metadataSummary(entry);
            return (
              <TableRow key={entry.id} hover>
                <TableCell className={classes.cellNoWrap}>
                  {new Date(entry.createdAt).toLocaleString()}
                </TableCell>
                <TableCell className={classes.cell}>
                  {auditActor(entry) ?? l10n.getString('audit-user-unknown')}
                </TableCell>
                <TableCell className={classes.cell}>
                  <span
                    className={classes.action}
                    style={{ color: actionColor(theme, entry.action) }}
                  >
                    {l10n.getString(actionLabelKey(entry.action))}
                  </span>
                </TableCell>
                <TableCell className={classes.cellNoWrap}>{entry.resourceType}</TableCell>
                <TableCell className={classes.cell}>
                  <span className={classes.resourceId}>{entry.resourceId ?? '—'}</span>
                </TableCell>
                <TableCell className={classes.cell} title={metadataRaw(entry)}>
                  {meta ? l10n.getString(meta.id, meta.args) : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <CardContent className={classes.cardContent} style={{ overflow: 'hidden' }}>
      <AuditListFilters />

      <div className={classes.tableWrapper}>{body()}</div>

      {list.total > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          handleChange={(_event, value) => goToPage(value)}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          goToPage={goToPage}
        />
      )}
    </CardContent>
  );
};

export default React.memo(AuditListLayout);
